import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import posthog from 'posthog-js';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import InternalSidebar from '../../components/InternalSidebar';
import InternalTopMenu from '../../components/InternalTopMenu';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';

const STATUS_VARIANTS = {
  success: 'bg-emerald-100 text-emerald-700',
  warning: 'bg-amber-100 text-amber-700',
  danger: 'bg-red-100 text-red-600',
  info: 'bg-blue-100 text-blue-600',
  neutral: 'bg-slate-100 text-slate-600',
};

const Campo = ({ rotulo, valor, children }) => (
  <div className="flex flex-col gap-1 border-b border-slate-100 py-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
    <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{rotulo}</span>
    <span className="break-all text-sm text-slate-700 sm:text-right">{children || valor || '—'}</span>
  </div>
);

const Bloco = ({ titulo, children }) => (
  <section className="rounded-3xl border border-slate-200 bg-white px-6 py-5 shadow-sm">
    <h2 className="mb-2 text-sm font-semibold text-slate-900">{titulo}</h2>
    {children}
  </section>
);

// O esqueleto ocupa a mesma área da página carregada, para o conteúdo não pular quando chega.
const Esqueleto = () => (
  <div className="flex flex-col gap-4">
    <div className="h-28 animate-pulse rounded-3xl bg-slate-200/70" />
    <div className="h-56 animate-pulse rounded-3xl bg-slate-200/70" />
    <div className="h-40 animate-pulse rounded-3xl bg-slate-200/70" />
  </div>
);

const TransactionDetail = () => {
  const { externalId } = useParams();
  const { logout } = useAuth();

  const [carregando, setCarregando] = useState(true);
  const [transacao, setTransacao] = useState(null);
  const [erro, setErro] = useState('');
  const [naoEncontrada, setNaoEncontrada] = useState(false);
  const [avisoDeCopia, setAvisoDeCopia] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const buscarDetalhe = useCallback(async () => {
    setCarregando(true);
    setErro('');
    setNaoEncontrada(false);

    try {
      const resposta = await api.get(`/transactions/${encodeURIComponent(externalId)}`);
      setTransacao(resposta.data);
    } catch (falha) {
      // 404 tem tela própria: não é erro de conexão, e oferecer "tentar de novo" para uma
      // transação que não existe só faria a pessoa insistir à toa.
      if (falha.response?.status === 404) {
        setNaoEncontrada(true);
      } else {
        setErro(
          falha.response?.data?.message ||
            falha.message ||
            'Não foi possível carregar os dados desta transação.'
        );
      }
    } finally {
      setCarregando(false);
    }
  }, [externalId]);

  useEffect(() => {
    buscarDetalhe();
  }, [buscarDetalhe]);

  useEffect(() => {
    posthog.capture('transaction_detail_opened', { externalId });
  }, [externalId]);

  const copiar = async (texto, evento) => {
    try {
      await navigator.clipboard.writeText(texto);
      setAvisoDeCopia('Copiado!');
      posthog.capture(evento, { externalId });
    } catch (falha) {
      setAvisoDeCopia('Não foi possível copiar.');
    }
    setTimeout(() => setAvisoDeCopia(''), 2000);
  };

  // O comprovante é texto simples, para caber em qualquer lugar onde a pessoa vá colar: WhatsApp,
  // e-mail ou o chamado que ela abriria no suporte.
  const comprovanteEmTexto = (dados) =>
    [
      'Comprovante Stackfy',
      `Identificador: ${dados.externalId}`,
      `Tipo: ${dados.type}`,
      `Status: ${dados.statusLabel}`,
      `Valor: R$ ${dados.amount?.formatted || '—'}`,
      `Taxa: R$ ${dados.fee?.formatted || '—'}`,
      `Valor líquido: R$ ${dados.netAmount?.formatted || '—'}`,
      `Criada em: ${dados.createdAt?.formatted || '—'}`,
      `Liquidada em: ${dados.settledAt?.formatted || '—'}`,
      dados.counterparty ? `Contraparte: ${dados.counterparty.name} (${dados.counterparty.document})` : '',
      dados.provider?.endToEndId ? `End-to-end id: ${dados.provider.endToEndId}` : '',
    ]
      .filter(Boolean)
      .join('\n');

  const conteudo = () => {
    if (carregando) return <Esqueleto />;

    if (naoEncontrada) {
      return (
        <div className="rounded-3xl border border-slate-200 bg-white px-6 py-16 text-center shadow-sm">
          <h2 className="text-base font-semibold text-slate-800">Transação não encontrada</h2>
          <p className="mt-1 text-sm text-slate-500">
            Este identificador não corresponde a nenhuma movimentação da sua conta.
          </p>
          <Link
            to="/transactions"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-700"
          >
            Voltar ao extrato
          </Link>
        </div>
      );
    }

    if (erro) {
      return (
        <div className="rounded-3xl border border-red-200 bg-red-50 px-6 py-10 text-center">
          <p className="text-sm text-red-600">{erro}</p>
          <button
            type="button"
            onClick={buscarDetalhe}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-700"
          >
            Tentar novamente
          </button>
        </div>
      );
    }

    if (!transacao) return null;

    const moeda = transacao.currency === 'BTC' ? 'BTC' : 'R$';
    const valorComMoeda = (valor) => {
      if (!valor) return '—';
      return moeda === 'BTC' ? `${valor.formatted} BTC` : `R$ ${valor.formatted}`;
    };
    const selo = STATUS_VARIANTS[transacao.statusTone] || STATUS_VARIANTS.neutral;

    return (
      <div className="flex flex-col gap-4">
        <section className="rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                {transacao.method}
              </span>
              <p className="text-3xl font-semibold text-slate-900" data-testid="detalhe-valor">
                {valorComMoeda(transacao.amount)}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-[10px] font-semibold uppercase text-slate-600">
                {transacao.type}
              </span>
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-[10px] font-semibold ${selo}`}
                data-testid="detalhe-status"
              >
                {transacao.statusLabel}
              </span>
            </div>
          </div>
        </section>

        <Bloco titulo="Resumo">
          <Campo rotulo="Valor bruto" valor={valorComMoeda(transacao.amount)} />
          <Campo rotulo="Taxa" valor={valorComMoeda(transacao.fee)} />
          <Campo rotulo="Valor líquido" valor={valorComMoeda(transacao.netAmount)} />
          <Campo rotulo="Saldo após a transação" valor={valorComMoeda(transacao.balanceAfter)} />
          <Campo rotulo="Criada em" valor={transacao.createdAt?.formatted} />
          <Campo rotulo="Liquidada em" valor={transacao.settledAt?.formatted} />
        </Bloco>

        {transacao.counterparty ? (
          <Bloco titulo={transacao.type === 'ENTRADA' ? 'Origem' : 'Destino'}>
            <Campo rotulo="Nome" valor={transacao.counterparty.name} />
            <Campo rotulo="Documento" valor={transacao.counterparty.document} />
            <Campo rotulo="Instituição" valor={transacao.counterparty.bank} />
            <Campo rotulo="Chave Pix" valor={transacao.counterparty.pixKey} />
          </Bloco>
        ) : null}

        <Bloco titulo="Identificadores">
          <Campo rotulo="Identificador">
            <span className="inline-flex items-center gap-2">
              <span data-testid="detalhe-external-id">{transacao.externalId}</span>
              <button
                type="button"
                onClick={() => copiar(transacao.externalId, 'transaction_detail_id_copied')}
                className="rounded-full border border-slate-200 px-3 py-1 text-[10px] font-semibold text-slate-600 transition-colors hover:bg-slate-50"
                aria-label="Copiar identificador"
              >
                Copiar
              </button>
            </span>
          </Campo>
          <Campo rotulo="End-to-end id" valor={transacao.provider?.endToEndId} />
          <Campo rotulo="Id no provedor" valor={transacao.provider?.biaTransactionId} />
        </Bloco>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Link
            to="/transactions"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-5 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-700"
          >
            Voltar ao extrato
          </Link>
          <button
            type="button"
            onClick={() => copiar(comprovanteEmTexto(transacao), 'transaction_detail_receipt_copied')}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 px-5 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
          >
            Copiar comprovante
          </button>
          {avisoDeCopia ? <span className="text-xs text-slate-500">{avisoDeCopia}</span> : null}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#f7f7f7] text-slate-700">
      <div className="min-h-screen flex flex-col lg:flex-row">
        <div className="lg:hidden">
          <div className="flex items-center justify-between px-6 py-4 bg-white shadow-sm">
            <button
              type="button"
              className="text-2xl text-slate-700"
              onClick={() => setSidebarOpen((aberto) => !aberto)}
              aria-label="Abrir menu"
            >
              <i className="bi-list"></i>
            </button>
            <img src="/img/branded/logob.png" alt="Stackfy" className="h-8" />
            <div className="w-7" />
          </div>
        </div>

        <InternalSidebar active="transactions" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {sidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fechar menu"
          ></button>
        )}

        <main className="flex-1 px-6 py-8 lg:px-10 lg:py-12">
          <EmailVerificationBanner />
          <header className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Detalhe da transação</h1>
              <p className="text-sm text-slate-500">
                <Link to="/transactions" className="underline hover:text-slate-700">
                  Extrato
                </Link>{' '}
                / {externalId}
              </p>
            </div>
            <InternalTopMenu onLogout={logout} />
          </header>

          <div className="mx-auto max-w-3xl">{conteudo()}</div>
        </main>
      </div>
    </div>
  );
};

export default TransactionDetail;
